import type { CryptogramLevel, GeneratedPuzzle, PuzzleCell } from './types';

function shuffle<T>(arr: T[], rand: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Deterministic PRNG seeded by level index so replays of the same level
// show the same cipher — mirrors Swift CryptogramGenerator behavior.
function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function generatePuzzle(level: CryptogramLevel, seed: number): GeneratedPuzzle {
  const rand = mulberry32(seed || 1);
  const text = level.text;
  const uniqueLetters = [
    ...new Set(text.toLowerCase().replace(/[^a-z]/g, '').split('')),
  ];
  const numbers = shuffle(
    Array.from({ length: 26 }, (_, i) => i + 1),
    rand,
  );
  const letterToNumber: Record<string, number> = {};
  uniqueLetters.forEach((l, i) => {
    letterToNumber[l] = numbers[i];
  });
  const numberToLetter: Record<number, string> = {};
  for (const [l, n] of Object.entries(letterToNumber)) {
    numberToLetter[n] = l.toUpperCase();
  }

  const cells: PuzzleCell[] = text.split('').map((ch, index) => {
    const lower = ch.toLowerCase();
    if (/[a-z]/.test(lower)) {
      return {
        index,
        char: ch,
        upper: ch.toUpperCase(),
        number: letterToNumber[lower],
        type: 'letter',
      };
    }
    if (ch === ' ') {
      return { index, char: ' ', upper: ' ', number: null, type: 'space' };
    }
    return { index, char: ch, upper: ch, number: null, type: 'punctuation' };
  });

  const words: PuzzleCell[][] = [];
  let buf: PuzzleCell[] = [];
  for (const c of cells) {
    if (c.type === 'space') {
      if (buf.length) {
        words.push(buf);
        buf = [];
      }
    } else {
      buf.push(c);
    }
  }
  if (buf.length) words.push(buf);

  return {
    text,
    author: level.author,
    cells,
    words,
    numberToLetter,
    uniqueLetters: uniqueLetters.map((l) => l.toUpperCase()),
  };
}

export interface StarterReveal {
  revealedNumbers: number[];
  revealedGuesses: Record<number, string>;
}

/** Reveal 2-4 most frequent letters as a starting foothold. */
export function pickStarterReveals(puzzle: GeneratedPuzzle): StarterReveal {
  const freq = new Map<number, number>();
  for (const c of puzzle.cells) {
    if (c.type === 'letter' && c.number != null) {
      freq.set(c.number, (freq.get(c.number) ?? 0) + 1);
    }
  }
  const len = puzzle.text.length;
  const revealCount = len < 50 ? 2 : len < 90 ? 3 : 4;
  const sorted = [...freq.entries()].sort((a, b) => b[1] - a[1]);
  const toReveal = sorted.slice(0, revealCount).map((e) => e[0]);
  const guesses: Record<number, string> = {};
  for (const n of toReveal) guesses[n] = puzzle.numberToLetter[n];
  return { revealedNumbers: toReveal, revealedGuesses: guesses };
}
